"use client";

import { Scale } from "@/animations/scale";
import Image from "@/components/Image";
import { cn } from "@/utils";
import { formatNumber } from "@/utils/number-format";
import { useResize } from "@/utils/useResize";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { HeaderFinder } from "./header-finder";
import { WrapperFinder } from "./wrapper-finder";
import { useHoldSwap } from "../apis/subgraph";

const variants = cva("w-full", {
  variants: {
    twSize: {
      xs: "max-w-[80%] mr-[14px] mt-[32px]",
      sm: "max-w-[80%] mr-[14px] mt-[32px]",
      md: "max-w-[80%] mr-[14px] mt-[32px]",
      lg: "max-w-[555px] mr-[32px] mt-[32px]",
      xl: "max-w-[493px] mr-[68px] -mt-[59px]",
      "2xl": "max-w-[524px] mr-[72px] -mt-[63px]",
      "3xl": "max-w-[555px] mr-[77px] -mt-[67px]",
    },
  },
  defaultVariants: {
    twSize: "2xl",
  },
});

export function FinderSwap() {
  const { twSize, width } = useResize();

  const { data } = useHoldSwap();
  //   totalValueLockedUsd -> hiển thị là TVL
  // totalVolumeUsd -> hiển thị là Total Volume
  // totalFeesUsd -> hiển thị là Total Fee
  // txCount -> hiển thị là Total Transation

  const TVL = useMemo(() => {
    const value = data?.data?.total_value_locked_usd;

    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data?.total_value_locked_usd]);

  const TotalVolume = useMemo(() => {
    const value = data?.data?.total_volume_usd;

    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data?.total_volume_usd]);

  const TotalFee = useMemo(() => {
    const value = data?.data?.total_fees_usd;

    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data?.total_fees_usd]);

  const TotalTransaction = useMemo(() => {
    const value = data?.data?.tx_count;

    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data?.tx_count]);

  return (
    <Scale>
      <div className="flex justify-end w-full">
        <WrapperFinder
          className={cn(variants({ twSize: twSize }), "overflow-hidden")}
        >
          <HeaderFinder>
            <p className="text-[#23262F] text-center text-[13px] font-medium leading-4 truncate">
              Holdstation Swap & Aggregator
            </p>
          </HeaderFinder>

          <div
            className="bg-no-repeat bg-top md:bg-center bg-cover flex flex-col justify-between"
            style={{
              background: "linear-gradient(180deg, #37508D 0%, #000F34 100%)",
            }}
          >
            <div className="p-5">
              <p className="text-xl lg:text-[28px] font-medium text-white leading-[1.42]">
                The Liquidity Engine of ZKsync, advanced AMM DEX and aggregator{" "}
                <br />{" "}
                <span className="text-[#D1D4DC]">
                  for Holdstation’s expanding ecosystem
                </span>
              </p>
            </div>

            <div className="flex-1 flex items-end lg:items-start mt-5 md:mt-10 px-5">
              <div className="grid grid-cols-2 gap-6 gap-y-4 gap-x-8">
                <div>
                  <p className="text-xl lg:text-[2.5rem] lg:leading-[3rem] font-medium text-[#fff] md:text-white">
                    ${TVL}
                  </p>
                  <p className="text-base font-medium text-[#777E90]">TVL</p>
                </div>

                <div>
                  <p className="text-xl lg:text-[2.5rem] lg:leading-[3rem] font-medium text-[#fff] md:text-white">
                    ${TotalVolume}
                  </p>
                  <p className="text-base font-medium text-[#777E90]">
                    Total Volume
                  </p>
                </div>

                <div>
                  <p className="text-xl lg:text-[2.5rem] lg:leading-[3rem] font-medium text-[#fff] md:text-white">
                    ${TotalFee}
                  </p>
                  <p className="text-base font-medium text-[#777E90]">
                    Total Fee
                  </p>
                </div>

                <div>
                  <p className="text-xl lg:text-[2.5rem] lg:leading-[3rem] font-medium text-[#fff] md:text-white">
                    {TotalTransaction}
                  </p>
                  <p className="text-base font-medium text-[#777E90]">
                    Total Transaction
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full -mt-4 md:-mt-[82px]">
              {width >= 768 ? (
                <Image
                  src={require("@/assets/bg/bg_swap.png")}
                  className=""
                  width={1659}
                  height={1152}
                  alt="swap"
                />
              ) : (
                <Image
                  src={require("@/assets/bg/bg_swap_mobile.png")}
                  className=""
                  width={900}
                  height={450}
                  alt="swap"
                />
              )}
            </div>

            <div
              className="grid grid-cols-[40px_1fr_112px] lg:grid-cols-[48px_1fr_112px] gap-2.5 lg:gap-4 p-4 bg-white/10 border-t border-t-white/10"
              style={{
                backdropFilter: "blur(12.5px)",
              }}
            >
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex-shrink-0">
                <Image
                  src={require("@/assets/icons/ic_swap.png")}
                  width={96}
                  height={96}
                  alt="swap"
                />
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-sm font-medium text-[#FFF] leading-5 line-clamp-1">
                  Holdstation Swap & Aggregator
                </p>
                <p className="text-sm font-medium text-[#777E90] leading-5 line-clamp-1">
                  Top swap DEX on ZKsync, optimized with AI enhancement
                </p>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to={"https://hold.so/swap"}
                  target="_blank"
                  data-action="redirect_hold.so"
                  className={clsx(
                    "px-6 py-2.5 rounded-full bg-white relative z-10",
                    "text-sm text-[#141416] font-medium whitespace-nowrap"
                  )}
                >
                  Explore
                </Link>
              </div>
            </div>
          </div>
        </WrapperFinder>
      </div>
    </Scale>
  );
}
